import { useRuntimeVariables } from 'app-kit'
import dayjs from 'dayjs'
import { useGeckoAuth } from 'features/auth'
import { useGeckoPermissions } from 'features/permissions'
import { MAX_SCORE_DAYS, ScoreRecentResponse } from 'features/sentinels/types'
import { getSensorColor } from 'features/timeSeries/config/timeSeriesColors'
import useSWR from 'swr'
import { PERMISSION_GECKO_INTERNAL_MEMBER } from 'types'

/** A sentinel the user has added to the chart. */
export interface ActiveSentinel {
  entryId: string
  /** Datapoint entry id of the sentinel's target tag (the pen it bands). */
  target: string
  /** Display name of the sentinel (from its Rolodex entry). */
  name?: string
  /** Chosen method key; undefined = first method in the score response. */
  method?: string
}

/** Per-sentinel scoring outcome; failures stay per-entry so one bad sentinel
 * doesn't blank the others. */
export type SentinelScoreResult =
  | { ok: true; response: ScoreRecentResponse }
  | { ok: false; error: string }

export interface SentinelScoreWindow {
  startDate: string
  endDate: string
  /** True when the visible range exceeds the service's scorable span and the
   * band only covers the trailing window. Consumers must surface this. */
  clamped: boolean
}

/**
 * Always score the widest accepted trailing window ending at the visible end
 * date, regardless of how far in the user has zoomed.
 *
 * Scoring is window-dependent on the service side: the cleaning plan's
 * quantile clips are computed over the pulled window, lag/rolling features
 * shift over the cleaned frame's rows, and missing history is center-filled
 * toward train-typical values. Deriving start_date from the visible `from`
 * therefore made the band genuinely change shape with zoom level. Anchoring
 * the window to the end date makes the response — and the rendered band — a
 * function of the visible end date only, and pan/zoom within the same day
 * reuses the cached response.
 *
 * Both request dates are INCLUSIVE: the service adds a day to end_date before
 * checking the span, so the widest accepted window is end_date minus
 * (MAX_SCORE_DAYS - 1) — [end - 14d, end] is 15 inclusive days and 422s.
 */
export function clampScoreWindow(timeRange: {
  from: string
  to: string
}): SentinelScoreWindow {
  const to = dayjs(timeRange.to)
  const from = dayjs(timeRange.from)
  const start = to.subtract(MAX_SCORE_DAYS - 1, 'day')
  return {
    startDate: start.format('YYYY-MM-DD'),
    endDate: to.format('YYYY-MM-DD'),
    clamped: from.isBefore(start, 'day'),
  }
}

/**
 * Score every active sentinel over the visible range via fim-service
 * (`POST /api/v1/sites/{site}/sentinel/score`). Ephemeral like the Dossiers
 * "Live check" panel: nothing is persisted. SWR keys on (site, sentinels,
 * window) so the sidebar and the chart overlay share one in-flight request.
 */
export const useSentinelScores = (
  siteId: string,
  activeSentinels: ActiveSentinel[],
  timeRange: { from: string; to: string },
  { enabled = true }: { enabled?: boolean } = {}
) => {
  const { FIM_SERVICE_URL } = useRuntimeVariables()
  const { authorizedAxios, isAuthenticated } = useGeckoAuth()
  const { hasPermissions } = useGeckoPermissions()

  const scoreWindow = clampScoreWindow(timeRange)
  const entryIds = activeSentinels.map((sentinel) => sentinel.entryId)

  // Sentinels are gecko-internal; a restored chart can carry them (and even
  // re-enable the data layer) for anyone it is shared with, so gate the
  // scoring calls here — the one choke point every surface goes through —
  // rather than relying on each UI gate.
  const shouldFetch =
    enabled &&
    isAuthenticated &&
    hasPermissions([PERMISSION_GECKO_INTERNAL_MEMBER]) &&
    siteId !== '' &&
    entryIds.length > 0

  const { data, error, isLoading, isValidating } = useSWR<
    Record<string, SentinelScoreResult>
  >(
    shouldFetch
      ? [
          'sentinel-scores',
          FIM_SERVICE_URL,
          siteId,
          entryIds.join(','),
          scoreWindow.startDate,
          scoreWindow.endDate,
        ]
      : null,
    async () => {
      const results = await Promise.all(
        entryIds.map(
          async (entryId): Promise<[string, SentinelScoreResult]> => {
            try {
              const res = await authorizedAxios.post(
                `${FIM_SERVICE_URL}/api/v1/sites/${encodeURIComponent(siteId)}/sentinel/score`,
                {
                  entry_id: entryId,
                  start_date: scoreWindow.startDate,
                  end_date: scoreWindow.endDate,
                }
              )
              const response = res.data as ScoreRecentResponse
              if (!response.ok) {
                return [
                  entryId,
                  { ok: false, error: response.error ?? 'scoring failed' },
                ]
              }
              return [entryId, { ok: true, response }]
            } catch (err) {
              return [
                entryId,
                {
                  ok: false,
                  error: err instanceof Error ? err.message : String(err),
                },
              ]
            }
          }
        )
      )
      return Object.fromEntries(results)
    },
    { keepPreviousData: true, revalidateOnFocus: false }
  )

  return {
    scores: data ?? {},
    scoreWindow,
    error,
    isLoading,
    isValidating,
  }
}

/** One sentinel's alerting range, ready for the uPlot band plugin. */
export interface SentinelBandOverlay {
  targetSensorId: string
  /** Display name of the sentinel driving this overlay. */
  sentinelName: string
  method: string
  label: string
  color: string
  /** Epoch-ms x positions, ascending (score cadence, e.g. 10 min). */
  xs: number[]
  upper: (number | null)[]
  lower: (number | null)[]
  /** The model's expected value — the band's center line. */
  center: (number | null)[]
  /** The scored actual target value per bucket (where the marks land). */
  actual: (number | null)[]
  /**
   * residual / err_scale per bucket — how many sigmas out the actual is,
   * SIGNED: positive above the expectation, negative below.
   */
  sigma: (number | null)[]
}

/** Sigma level at which a point counts as out of band on the chart. */
export const SIGMA_WARN = 2
/**
 * The one color for every out-of-band affordance (glow, lane, columns,
 * tooltip callout) — matches the thresholds layer blue. Deliberately not an
 * alarm palette.
 */
export const SENTINEL_OUT_OF_BAND_COLOR = '#60a5fa'

/** Score cadence assumed when a response has too few buckets to measure. */
export const DEFAULT_SCORE_CADENCE_MS = 10 * 60 * 1000

/**
 * The score cadence measured from the buckets themselves: the smallest
 * positive spacing. The service re-applies the sentinel's cleaning plan when
 * scoring, and gate ops DROP rows, so `xs` is NOT uniformly spaced — any
 * single pair (in particular `xs[1] - xs[0]`) can straddle a multi-hour gap.
 * The minimum spacing recovers the underlying interval_minutes because any
 * two consecutive surviving rows sit one interval apart.
 */
export function scoreCadenceMs(xs: number[]): number {
  let smallest = Infinity
  for (let i = 1; i < xs.length; i++) {
    const delta = xs[i] - xs[i - 1]
    if (delta > 0 && delta < smallest) smallest = delta
  }
  return Number.isFinite(smallest) ? smallest : DEFAULT_SCORE_CADENCE_MS
}

/**
 * Index of the bucket whose timestamp is closest to `timestampMs` (no
 * distance limit — callers apply their own tolerance). Binary search over the
 * ascending `xs`, so per-sample matching in the draw hooks stays cheap.
 */
export function nearestBucketIndex(
  xs: number[],
  timestampMs: number
): number | null {
  if (xs.length === 0) return null
  let lo = 0
  let hi = xs.length - 1
  if (timestampMs <= xs[0]) return 0
  if (timestampMs >= xs[hi]) return hi
  while (hi - lo > 1) {
    const mid = (lo + hi) >> 1
    if (xs[mid] <= timestampMs) lo = mid
    else hi = mid
  }
  return timestampMs - xs[lo] <= xs[hi] - timestampMs ? lo : hi
}

/**
 * Index of the score bucket nearest a chart timestamp, within ~a bucket's
 * width, so lookups tolerate the chart cadence differing from the score
 * cadence. Timestamps that fall inside a cleaning gap (no bucket within
 * three-quarters of the cadence) answer null rather than snapping to a
 * far-away bucket.
 */
export function bucketIndexAtTime(
  overlay: Pick<SentinelBandOverlay, 'xs'>,
  timestampMs: number
): number | null {
  const { xs } = overlay
  const index = nearestBucketIndex(xs, timestampMs)
  if (index === null) return null
  const cadence = scoreCadenceMs(xs)
  return Math.abs(xs[index] - timestampMs) > cadence * 0.75 ? null : index
}

/** Signed sigmas-out at (or near) a chart timestamp. */
export function sigmaAtTime(
  overlay: Pick<SentinelBandOverlay, 'xs' | 'sigma'>,
  timestampMs: number
): number | null {
  const index = bucketIndexAtTime(overlay, timestampMs)
  return index === null ? null : (overlay.sigma[index] ?? null)
}

/** The model's expected value at (or near) a chart timestamp. */
export function expectedAtTime(
  overlay: Pick<SentinelBandOverlay, 'xs' | 'center'>,
  timestampMs: number
): number | null {
  const index = bucketIndexAtTime(overlay, timestampMs)
  return index === null ? null : (overlay.center[index] ?? null)
}

/**
 * The sentinel's prediction and signed sigma for a legend/drawer row: at the
 * row's displayed timestamp (cursor-following) when available, else the
 * latest scored bucket.
 */
export function sentinelReadingFor(
  overlay: SentinelBandOverlay | undefined,
  xValue: string | number | null | undefined
): { predicted: number; sigma: number } | null {
  if (!overlay) return null
  const timestampMs =
    typeof xValue === 'number'
      ? xValue
      : typeof xValue === 'string'
        ? new Date(xValue).getTime()
        : null
  if (timestampMs !== null && Number.isFinite(timestampMs)) {
    const predicted = expectedAtTime(overlay, timestampMs)
    const sigma = sigmaAtTime(overlay, timestampMs)
    if (predicted !== null && sigma !== null) return { predicted, sigma }
  }
  const latest = latestOverlayReading(overlay)
  return latest ? { predicted: latest.expected, sigma: latest.sigma } : null
}

/**
 * The most recent bucket with both an expected value and a sigma — the
 * sentinel's live status readout (used by the tag-names sidebar).
 */
export function latestOverlayReading(
  overlay: Pick<SentinelBandOverlay, 'center' | 'sigma'>
): { expected: number; sigma: number } | null {
  for (let i = overlay.center.length - 1; i >= 0; i--) {
    const expected = overlay.center[i]
    const sigma = overlay.sigma[i]
    if (
      expected !== null &&
      expected !== undefined &&
      sigma !== null &&
      sigma !== undefined
    ) {
      return { expected, sigma }
    }
  }
  return null
}

/**
 * Turn score responses into drawable band edges: `expected ± residual_band`
 * for each sentinel's chosen method (defaulting to the first method the
 * service returned). Colored like the target pen so the association is
 * obvious on the chart.
 */
export function buildSentinelBandOverlays({
  activeSentinels,
  scores,
  allSensorsToRender,
  customSensorColors,
}: {
  activeSentinels: ActiveSentinel[]
  scores: Record<string, SentinelScoreResult>
  allSensorsToRender: Array<{ id: string; name: string }>
  customSensorColors?: Record<string, string>
}): SentinelBandOverlay[] {
  const overlays: SentinelBandOverlay[] = []

  for (const sentinel of activeSentinels) {
    const score = scores[sentinel.entryId]
    if (!score?.ok) continue
    const { response } = score
    const methods = response.methods ?? {}
    const methodKey =
      sentinel.method && methods[sentinel.method]
        ? sentinel.method
        : Object.keys(methods)[0]
    if (!methodKey) continue
    const method = methods[methodKey]

    const targetKey = response.target ?? sentinel.target
    const expected = method.expected[targetKey]
    const band = method.residual_band[targetKey]
    if (!expected || band === null || band === undefined) continue

    const xs = (response.ts ?? []).map((ts) => new Date(ts).getTime())
    if (xs.length === 0 || xs.length !== expected.length) continue

    const penIndex = allSensorsToRender.findIndex(
      (sensor) => sensor.id === sentinel.target
    )
    const color =
      customSensorColors?.[sentinel.target] ??
      getSensorColor(penIndex >= 0 ? penIndex : 0)

    const residual = method.residual[targetKey]
    const errScale = method.err_scale[targetKey]
    const actual = response.actual?.[targetKey]
    const sigma = xs.map((_, i) => {
      const r = residual?.[i]
      if (r === null || r === undefined || !errScale) return null
      return r / errScale
    })

    overlays.push({
      targetSensorId: sentinel.target,
      sentinelName: sentinel.name ?? sentinel.entryId,
      method: methodKey,
      label: `${targetKey} ±${method.band_k}σ`,
      color,
      xs,
      upper: expected.map((value) => (value === null ? null : value + band)),
      lower: expected.map((value) => (value === null ? null : value - band)),
      center: expected,
      actual: actual ?? xs.map(() => null),
      sigma,
    })
  }

  return overlays
}

/**
 * Cheap order-sensitive checksum over an overlay's drawable values, for the
 * chart-recreation signature. Endpoint + count digests alone can miss a
 * response whose interior values changed (same surviving buckets, different
 * expected/band) — the retained uPlot options would then keep drawing the
 * previous window's band through a stale plugin closure.
 */
export function sentinelOverlayValuesDigest(
  overlay: Pick<
    SentinelBandOverlay,
    'xs' | 'upper' | 'lower' | 'center' | 'actual' | 'sigma'
  >
): number {
  const bits = new DataView(new ArrayBuffer(8))
  let hash = 0
  const mix = (value: number | null | undefined) => {
    // Nulls perturb the hash too, so a value appearing or disappearing at
    // the same index still changes the digest.
    bits.setFloat64(0, value === null || value === undefined ? NaN : value)
    hash = Math.imul(hash ^ bits.getInt32(0), 2246822519)
    hash = Math.imul(hash ^ bits.getInt32(4), 2654435761)
  }
  for (let i = 0; i < overlay.xs.length; i++) {
    mix(overlay.xs[i])
    mix(overlay.upper[i])
    mix(overlay.lower[i])
    mix(overlay.center[i])
    mix(overlay.actual[i])
    mix(overlay.sigma[i])
  }
  return hash
}
